import type { FC, ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'
import Lenis from 'lenis'

type ScrollTarget = string | number | HTMLElement

interface ScrollOptions {
    immediate?: boolean
}

interface SmoothScrollContextValue {
    lenis: Lenis | null
    scrollTo: (target: ScrollTarget, options?: ScrollOptions) => void
}

interface SmoothScrollProps {
    children: ReactNode
}

const SmoothScrollContext = createContext<SmoothScrollContextValue>({
    lenis: null,
    scrollTo: () => undefined
})

export const useSmoothScroll = (): SmoothScrollContextValue => useContext(SmoothScrollContext)

export const SmoothScroll: FC<SmoothScrollProps> = ({ children }): ReactNode => {
    const [lenis, setLenis] = useState<Lenis | null>(null)

    useEffect(() => {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
        let instance: Lenis | null = null

        const create = (): void => {
            if (instance) return

            instance = new Lenis({
                lerp: 0.09,
                autoRaf: true,
                stopInertiaOnNavigate: true
            })

            setLenis(instance)
        }

        const destroy = (): void => {
            if (!instance) return

            instance.destroy()
            instance = null
            setLenis(null)
        }

        const sync = (): void => {
            if (reducedMotion.matches) destroy()
            else create()
        }

        sync()
        reducedMotion.addEventListener('change', sync)

        return () => {
            reducedMotion.removeEventListener('change', sync)
            destroy()
        }
    }, [])

    const scrollTo = useCallback((target: ScrollTarget, options: ScrollOptions = {}): void => {
        let destination: number | HTMLElement

        if (typeof target === 'string') {
            const element = document.querySelector<HTMLElement>(target)

            if (!element) return

            destination = element
        }
        else {
            destination = target
        }

        if (lenis) {
            lenis.scrollTo(destination, {
                immediate: options.immediate,
                duration: options.immediate ? undefined : 1.6,
                force: true
            })

            return
        }

        const top = typeof destination === 'number'
            ? destination
            : destination.getBoundingClientRect().top + window.scrollY
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        window.scrollTo({ top, behavior: options.immediate || reduce ? 'auto' : 'smooth' })
    }, [lenis])

    const value = useMemo(() => ({ lenis, scrollTo }), [lenis, scrollTo])

    return (
        <SmoothScrollContext.Provider value={value}>
            {children}
        </SmoothScrollContext.Provider>
    )
}
